﻿import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'

const baseURL = (import.meta.env.VITE_API_BASE_URL || (import.meta.env.PROD ? window.location.origin : 'http://127.0.0.1:8000')).replace(/\/$/, '')
const ACCESS_KEY = 'yl_access'
const REFRESH_KEY = 'yl_refresh'
let authGeneration = 0
let refreshing: { generation: number; token: string; promise: Promise<string> } | null = null
export const api = axios.create({ baseURL, timeout: 20000, headers: { 'Content-Type': 'application/json' } })
const authenticationEndpoint = (url?: string) => /\/api\/auth\/(token|register)\//.test(url || '')

export async function refreshAccess(): Promise<string> {
  const token = localStorage.getItem(REFRESH_KEY)
  if (!token) throw new Error('Session absente')
  const generation = authGeneration
  if (refreshing?.generation === generation && refreshing.token === token) return refreshing.promise
  const promise = axios.post<{ access: string; refresh?: string }>(`${baseURL}/api/auth/token/refresh/`, { refresh: token }, { timeout: 20000 }).then(({ data }) => {
    if (generation !== authGeneration || localStorage.getItem(REFRESH_KEY) !== token) throw new Error('Session remplacée')
    if (!data.access) throw new Error('Réponse de renouvellement invalide')
    sessionStorage.setItem(ACCESS_KEY, data.access)
    if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh)
    return data.access
  }).catch((error: unknown) => {
    if (generation === authGeneration && axios.isAxiosError(error) && [400,401,403].includes(error.response?.status || 0)) expireSession()
    throw error
  }).finally(() => { if (refreshing?.promise === promise) refreshing = null })
  refreshing = { generation, token, promise }
  return promise
}

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (authenticationEndpoint(config.url)) return config
  const request = config as InternalAxiosRequestConfig & { _generation?: number }
  if (request._generation !== undefined && request._generation !== authGeneration) throw new axios.CanceledError('Session remplacée')
  request._generation = authGeneration
  let token = sessionStorage.getItem(ACCESS_KEY)
  if (!token && localStorage.getItem(REFRESH_KEY)) token = await refreshAccess()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})
api.interceptors.response.use(undefined, async (error: AxiosError) => {
  const request = error.config as (InternalAxiosRequestConfig & { _retry?: boolean; _generation?: number }) | undefined
  if (error.response?.status !== 401 || !request || authenticationEndpoint(request.url)) throw error
  if (request._generation !== authGeneration) throw error
  if (request._retry || !localStorage.getItem(REFRESH_KEY)) { expireSession(); throw error }
  request._retry = true
  const failedToken = String(request.headers.Authorization || '').replace(/^Bearer /, '')
  const currentToken = sessionStorage.getItem(ACCESS_KEY)
  const access = currentToken && currentToken !== failedToken ? currentToken : await refreshAccess()
  request.headers.Authorization = `Bearer ${access}`
  return api(request)
})
export function setTokens(access: string, refresh: string) { authGeneration++; sessionStorage.setItem(ACCESS_KEY, access); localStorage.setItem(REFRESH_KEY, refresh) }
export function clearTokens() { authGeneration++; sessionStorage.removeItem(ACCESS_KEY); localStorage.removeItem(REFRESH_KEY); localStorage.removeItem('yl_user') }
function expireSession() { clearTokens(); window.dispatchEvent(new Event('youloto:session-expired')) }
export function hasSession() { return Boolean(sessionStorage.getItem(ACCESS_KEY) || localStorage.getItem(REFRESH_KEY)) }
export function apiError(error: unknown, fallback = 'Une erreur est survenue. Veuillez réessayer.') {
  if (!axios.isAxiosError(error)) return fallback
  const status = error.response?.status
  if (!error.response) return error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' ? 'Le serveur met trop de temps à répondre.' : 'Connexion au serveur impossible. Vérifiez votre accès internet.'
  if (status && status >= 500) return 'Le serveur Youloto est temporairement indisponible. Réessayez dans quelques instants.'
  const labels: Record<string, string> = { phone: 'Téléphone', phone_number: 'Téléphone', target_phone: 'Bénéficiaire', password: 'Mot de passe', current_password: 'Mot de passe actuel', new_password: 'Nouveau mot de passe', new_password_confirm: 'Confirmation', email: 'E-mail', whatsapp: 'WhatsApp', amount: 'Montant', montant: 'Montant' }
  const flatten = (value: unknown): string => {
    if (typeof value === 'string') return value.includes('<html') || value.includes('<!DOCTYPE') ? '' : value
    if (Array.isArray(value)) return value.map(flatten).filter(Boolean).join(' ')
    if (value && typeof value === 'object') return Object.entries(value).map(([key,v]) => `${labels[key] ? `${labels[key]} : ` : ''}${flatten(v)}`).filter(Boolean).join('\n')
    return ''
  }
  const data = error.response.data as Record<string, unknown> | undefined
  if (data && typeof data === 'object') {
    const message = flatten(data.detail || data.message || data)
    if (message.trim()) return message
  }
  return ({ 400:'Vérifiez les informations saisies.', 401:'Votre session a expiré. Reconnectez-vous.', 403:'Vous ne disposez pas des autorisations nécessaires.', 404:'La ressource demandée est introuvable.', 409:'Les données ont changé. Actualisez avant de réessayer.', 422:'Vérifiez les informations saisies.' } as Record<number,string>)[status || 0] || fallback
}
